import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import Card from './Card';
import { CSS_CLASSES, CSS_STYLES, ANIMATIONS } from '../lib/styles';

interface StatCardProps {
  label: string;
  value: string | number;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'indigo' | 'red' | 'yellow';
  trend?: {
    value: string | number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  onClick?: () => void;
  className?: string;
  animation?: {
    initial?: any;
    animate?: any;
    transition?: any;
    whileHover?: any;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  color = 'blue',
  trend,
  onClick,
  className = '',
  animation = ANIMATIONS.FADE_IN
}) => {
  const getColorClasses = (color: string) => {
    const colorMap = {
      green: 'bg-gradient-to-br from-green-500 to-emerald-600',
      blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
      purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
      orange: 'bg-gradient-to-br from-orange-500 to-orange-600',
      pink: 'bg-gradient-to-br from-pink-500 to-pink-600',
      indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
      red: 'bg-gradient-to-br from-red-500 to-red-600',
      yellow: 'bg-gradient-to-br from-yellow-500 to-yellow-600'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getTrendIcon = (type: string) => {
    switch (type) {
      case 'increase':
        return <TrendingUp className={CSS_CLASSES.ICON_SMALL} />;
      case 'decrease':
        return <TrendingDown className={CSS_CLASSES.ICON_SMALL} />;
      default:
        return <Minus className={CSS_CLASSES.ICON_SMALL} />;
    }
  };

  const getTrendColor = (type: string) => {
    switch (type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <Card 
      onClick={onClick} 
      animation={animation}
      className={`${CSS_CLASSES.CARD_BASE} ${onClick ? CSS_CLASSES.CARD_CLICKABLE : ''} ${className}`}
      hover={!!onClick}
    >
      <div style={CSS_STYLES.CARD}>
        <div className="p-4">
          <div className={CSS_CLASSES.FLEX_BETWEEN}>
            {Icon && (
              <div className={`p-2 rounded-lg shadow-lg ${getColorClasses(color)}`}>
                <Icon className={CSS_CLASSES.ICON_MEDIUM} style={{ color: '#ffffff' }} />
              </div>
            )}
            {trend && (
              <div className={`flex items-center ${getTrendColor(trend.type)}`}>
                {getTrendIcon(trend.type)}
                <span className="text-sm font-semibold ml-1">{trend.value}</span>
              </div>
            )}
          </div>
          <div className="mt-3">
            <p className={CSS_CLASSES.TEXT_SECONDARY} style={CSS_STYLES.TEXT_SECONDARY}>
              {label}
            </p>
            <p className="text-xl font-bold mt-1" style={CSS_STYLES.TEXT_PRIMARY}>
              {value}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default StatCard;
